import React, { useRef, useEffect, useState, memo } from "react";
import { motion, useInView, useMotionValue, useSpring } from "framer-motion";
import { useNavigate } from "react-router-dom";

/* ─────────────────────────────────────────────────────────────────
   PERF FIXES:
   1. Counter — spring only runs once (on inView), not looping
   2. TiltCard — passive mousemove, rAF throttled
   3. TimelineItem — CSS pulse dot instead of Framer animate
   4. StatCard glow — CSS hover, not Framer whileHover motion.div
   5. All looping animations → CSS keyframes
───────────────────────────────────────────────────────────────── */

/* ── Animated counter — fires once on scroll-into-view ── */
function Counter({ target, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const count = useMotionValue(0);
  const spring = useSpring(count, { stiffness: 55, damping: 22, restDelta: 0.5 });
  const [val, setVal] = useState("0");

  useEffect(() => {
    const unsub = spring.on("change", v => setVal(Math.floor(v).toString()));
    return unsub;
  }, [spring]);

  useEffect(() => {
    if (inView && target !== "∞") count.set(parseInt(target));
  }, [inView, target, count]);

  return (
    <span ref={ref} style={{
      fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 42,
      background: "linear-gradient(135deg,#a855f7,#e879f9)",
      WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
    }}>
      {target === "∞" ? "∞" : val + suffix}
    </span>
  );
}

/* ── Stat card — CSS hover glow (not Framer motion.div) ── */
const StatCard = memo(function StatCard({ value, suffix, label, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }} transition={{ duration: 0.65, delay }}
      whileHover={{ y: -6 }}
      className="stat-card"
      style={{
        padding: "32px 24px", borderRadius: 20, textAlign: "center",
        background: "rgba(255,255,255,0.025)", border: "1px solid rgba(124,58,237,0.15)",
        cursor: "default", position: "relative", overflow: "hidden",
        transition: "box-shadow 0.3s ease,border-color 0.3s ease",
      }}>
      <Counter target={value} suffix={suffix} />
      <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: "rgba(241,240,245,0.45)", marginTop: 6 }}>{label}</div>
    </motion.div>
  );
});

/* ── Tilt card — rAF throttled, no Framer spring per frame ── */
function TiltCard({ icon, title, text, color, delay }) {
  const cardRef = useRef(null);
  const rafRef = useRef(null);

  const handleMouse = (e) => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      if (!cardRef.current) { rafRef.current = null; return; }
      const rect = cardRef.current.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const rx = ((e.clientY - cy) / (rect.height / 2)) * -7;
      const ry = ((e.clientX - cx) / (rect.width / 2)) * 7;
      cardRef.current.style.transform = `perspective(800px) rotateX(${rx}deg) rotateY(${ry}deg)`;
      rafRef.current = null;
    });
  };

  const handleLeave = () => {
    if (cardRef.current)
      cardRef.current.style.transform = "perspective(800px) rotateX(0deg) rotateY(0deg)";
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }} transition={{ duration: 0.65, delay }}>
      <div ref={cardRef} onMouseMove={handleMouse} onMouseLeave={handleLeave}
        style={{ transition: "transform 0.15s ease", willChange: "transform" }}>
        <div className="tilt-inner" style={{
          padding: "36px 28px", borderRadius: 20,
          background: "rgba(255,255,255,0.025)", border: "1px solid rgba(124,58,237,0.15)",
          cursor: "default", position: "relative", overflow: "hidden",
          transition: "border-color 0.3s ease,box-shadow 0.3s ease",
        }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = `${color}50`;
            e.currentTarget.style.boxShadow = `0 0 40px ${color}20`;
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = "rgba(124,58,237,0.15)";
            e.currentTarget.style.boxShadow = "none";
          }}>
          <div style={{
            position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%",
            background: `radial-gradient(circle,${color}20,transparent 70%)`, pointerEvents: "none"
          }} />
          <span style={{ fontSize: 32, marginBottom: 16, display: "block" }}>{icon}</span>
          <h3 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 18, color: "#f1f0f5", marginBottom: 10 }}>{title}</h3>
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 14, color: "rgba(241,240,245,0.48)", lineHeight: 1.75 }}>{text}</p>
        </div>
      </div>
    </motion.div>
  );
}

/* ── Timeline item — CSS pulse dot ── */
function TimelineItem({ year, title, desc, delay, side }) {
  return (
    <>
      <style>{`
        @keyframes tlPulse {
          0%  { box-shadow: 0 0 0 0 rgba(168,85,247,0.5); }
          70% { box-shadow: 0 0 0 10px rgba(168,85,247,0); }
          100%{ box-shadow: 0 0 0 0 rgba(168,85,247,0); }
        }
      `}</style>
      <motion.div className="about-timeline-item" initial={{ opacity: 0, x: side === "left" ? -36 : 36 }}
        whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
        transition={{ duration: 0.75, delay, ease: [0.16, 1, 0.3, 1] }}
        style={{
          display: "flex", flexDirection: side === "left" ? "row" : "row-reverse",
          gap: 32, alignItems: "flex-start", marginBottom: 48
        }}>
        <div className="about-timeline-content" style={{ flex: 1, textAlign: side === "left" ? "right" : "left" }}>
          <div
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 0 32px rgba(124,58,237,0.2)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
            style={{
              padding: "24px 28px", borderRadius: 16,
              background: "rgba(255,255,255,0.025)", border: "1px solid rgba(124,58,237,0.15)",
              transition: "transform 0.3s ease,box-shadow 0.3s ease",
            }}>
            <span style={{
              fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 600,
              color: "#a855f7", letterSpacing: "0.1em", textTransform: "uppercase"
            }}>{year}</span>
            <h4 style={{
              fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 17,
              color: "#f1f0f5", margin: "8px 0"
            }}>{title}</h4>
            <p style={{
              fontFamily: "'DM Sans',sans-serif", fontSize: 14,
              color: "rgba(241,240,245,0.45)", lineHeight: 1.7
            }}>{desc}</p>
          </div>
        </div>
        {/* Center dot — CSS pulse */}
        <div className="about-timeline-center" style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
          <div style={{
            width: 16, height: 16, borderRadius: "50%",
            background: "linear-gradient(135deg,#7c3aed,#e879f9)",
            border: "2px solid rgba(124,58,237,0.5)",
            animation: `tlPulse 2.5s ${delay}s ease-out infinite`,
            willChange: "box-shadow",
          }} />
          <div style={{ width: 1, height: 80, background: "linear-gradient(to bottom,rgba(124,58,237,0.4),transparent)" }} />
        </div>
        <div className="about-timeline-spacer" style={{ flex: 1 }} />
      </motion.div>
    </>
  );
}

const STATS = [
  { value: "100", suffix: "+", label: "Projects Shipped", delay: 0 },
  { value: "50", suffix: "+", label: "Happy Clients", delay: 0.1 },
  { value: "3", suffix: "+", label: "Years in the Dark", delay: 0.2 },
  { value: "∞", suffix: "", label: "Lines of Code", delay: 0.3 },
];

const VALUES = [
  {
    icon: "⬡", title: "Philosophy", color: "#7c3aed",
    text: "Silence is stronger than noise. Precision is stronger than chaos. Every interface carries intention."
  },
  {
    icon: "◈", title: "Vision", color: "#a855f7",
    text: "To build systems that feel alive — immersive environments where design, performance, and emotion coexist."
  },
  {
    icon: "◎", title: "Craft", color: "#e879f9",
    text: "We don't build websites. We engineer digital experiences that breathe. Every pixel deliberate."
  },
];

const TIMELINE = [
  { year: "2021", title: "NyxLabs Founded", desc: "Started in a dark room with a laptop and an obsession for building beautiful things.", side: "left" },
  { year: "2022", title: "First 20 Clients", desc: "Shipped 30+ projects — web apps, landing pages, and immersive experiences.", side: "right" },
  { year: "2023", title: "Design System", desc: "Built our own void-based design system used across every NyxLabs product.", side: "left" },
  { year: "2024", title: "100+ Projects", desc: "Crossed the milestone. Still going deeper into the dark with every build.", side: "right" },
];

export default function About() {
  const navigate = useNavigate();

  return (
    <div className="about-page" style={{ minHeight: "100vh", padding: "120px 60px 80px", maxWidth: 1200, margin: "0 auto" }}>
      <style>{`
        .stat-card:hover { box-shadow:0 0 40px rgba(124,58,237,0.22); border-color:rgba(124,58,237,0.35) !important; }
      `}</style>

      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }} transition={{ duration: 0.8 }} style={{ marginBottom: 72 }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 16px",
          borderRadius: 100, background: "rgba(124,58,237,0.1)", border: "1px solid rgba(124,58,237,0.25)",
          marginBottom: 20,
        }}>
          <span style={{
            fontSize: 11, fontFamily: "'DM Sans',sans-serif", fontWeight: 500,
            color: "#c084fc", letterSpacing: "0.1em", textTransform: "uppercase"
          }}>About NyxLabs</span>
        </div>
        <h1 style={{
          fontFamily: "'Syne',sans-serif", fontWeight: 800,
          fontSize: "clamp(38px,6vw,72px)", lineHeight: 1.05, letterSpacing: "-0.02em",
          color: "#f1f0f5", marginBottom: 20,
        }}>
          Crafted in Darkness,<br />
          <span style={{
            background: "linear-gradient(135deg,#a855f7,#e879f9)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text"
          }}>
            Engineered with Precision
          </span>
        </h1>
        <p style={{
          fontFamily: "'DM Sans',sans-serif", fontWeight: 300, fontSize: 18,
          color: "rgba(241,240,245,0.5)", maxWidth: 600, lineHeight: 1.75
        }}>
          We create digital experiences that are powerful, minimal, and built to last.
          Every detail crafted with clarity and purpose.
        </p>
      </motion.div>

      {/* Stats */}
      <div className="about-stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 96 }}>
        {STATS.map((s, i) => <StatCard key={i} {...s} />)}
      </div>

      {/* Tilt cards */}
      <div style={{ marginBottom: 96 }}>
        <motion.h2 initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
          style={{
            fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 36,
            color: "#f1f0f5", textAlign: "center", marginBottom: 48
          }}>
          Our{" "}
          <span style={{
            background: "linear-gradient(135deg,#a855f7,#e879f9)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text"
          }}>
            Core Beliefs
          </span>
        </motion.h2>
        <div className="about-values-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20 }}>
          {VALUES.map((v, i) => <TiltCard key={i} {...v} delay={i * 0.12} />)}
        </div>
      </div>

      {/* Timeline */}
      <div style={{ marginBottom: 80 }}>
        <motion.h2 initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
          style={{
            fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 36,
            color: "#f1f0f5", textAlign: "center", marginBottom: 64
          }}>
          Our{" "}
          <span style={{
            background: "linear-gradient(135deg,#a855f7,#e879f9)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text"
          }}>
            Journey
          </span>
        </motion.h2>
        {TIMELINE.map((t, i) => <TimelineItem key={i} {...t} delay={i * 0.15} />)}
      </div>

      {/* CTA */}
      <motion.div initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }} transition={{ duration: 0.8 }}
        style={{
          padding: "52px 48px", borderRadius: 24,
          background: "linear-gradient(135deg,rgba(124,58,237,0.1),rgba(232,121,249,0.06))",
          border: "1px solid rgba(124,58,237,0.2)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: "wrap", gap: 24,
          transition: "box-shadow 0.3s ease",
        }}
        whileHover={{ boxShadow: "0 0 60px rgba(124,58,237,0.2)" }}>
        <div>
          <p style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 24, color: "#f1f0f5", marginBottom: 8 }}>
            Ready to build something dark?
          </p>
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 14, color: "rgba(241,240,245,0.45)" }}>
            Let's craft your next immersive experience.
          </p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(124,58,237,0.5)" }}
          whileTap={{ scale: 0.97 }} onClick={() => navigate("/contact")}
          style={{
            padding: "14px 36px", borderRadius: 12, cursor: "pointer",
            background: "linear-gradient(135deg,#7c3aed,#a855f7)",
            border: "none", color: "#fff",
            fontFamily: "'DM Sans',sans-serif", fontWeight: 700, fontSize: 14,
            boxShadow: "0 0 24px rgba(124,58,237,0.35)",
          }}>
          Get in Touch →
        </motion.button>
      </motion.div>

      <style>{`
        @media (max-width: 992px) {
          .about-page { padding: 110px 30px 70px !important; }
          .about-stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 768px) {
          .about-page { padding: 100px 20px 60px !important; }
          .about-values-grid { grid-template-columns: 1fr !important; }
          .about-timeline-item {
            flex-direction: column !important;
            gap: 16px !important;
            margin-bottom: 28px !important;
          }
          .about-timeline-content { text-align: left !important; width: 100%; }
          .about-timeline-center { flex-direction: row !important; gap: 10px !important; }
          .about-timeline-center > div:last-child { width: 36px !important; height: 1px !important; }
          .about-timeline-spacer { display: none !important; }
        }
        @media (max-width: 520px) {
          .about-stats-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
